import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import FicheChantier from './FicheChantier';
import FicheDocuments from './FicheDocuments';

// ============================================
// MAPPING DES RÔLES UTILISATEURS
// (codes backend → libellés affichés dans l'historique)
// ============================================
const ROLE_LABELS = {
  'admin': 'Admin',
  'commercial': 'Commercial',
  'manager_vente': 'Manager Vente',
  'employe_vente': 'Employé Vente'
};

// ============================================
// PAGINATION DE L'HISTORIQUE
// (nombre d'événements affichés par page)
// ============================================
const HISTORY_PAGE_SIZE = 6;

// ============================================
// LIBELLÉS DES CHAMPS POUR L'HISTORIQUE DES MODIFICATIONS
// (clé interne formData → libellé français)
// Couvre les 4 onglets : Client, Logement, Technique, Finance
// ============================================
const FIELD_LABELS = {
  // Client
  nom: 'Nom',
  prenom: 'Prénom',
  tel: 'Téléphone',
  mail: 'Email',
  adresse: 'Adresse',
  codePostal: 'Code postal',
  ville: 'Ville',
  // Logement
  tempsProprietaire: 'Propriétaire depuis',
  ageMaison: 'Âge maison',
  surfaceHabitable: 'Surface habitable',
  chargesAnnuelles: 'Charges annuelles',
  modeChauffage: 'Mode chauffage',
  // Technique
  alimentationCompteur: 'Alimentation compteur',
  orientationToit: 'Orientation toit',
  inclinaisonToiture: 'Inclinaison toiture',
  elementsSurToiture: 'Éléments toit',
  revetementToit: 'Revêtement toit',
  puissanceInstallation: 'Puissance installation',
  puissanceCustomKw: 'Puissance custom (kW)',
  // Finance
  numeroDevis: 'Numéro devis',
  prixVendu: 'Prix vendu',
  puissanceKwc: 'Puissance kWc',
  modePaiement: 'Mode de paiement',
  dateDevis: 'Date du devis',
  dateSignature: 'Date de signature'
};

// Libellés affichés pour les éléments du multi-select "elementsSurToiture"
const ELEMENT_TOIT_LABELS = {
  velux: 'Velux',
  chienAssis: 'Chien-assis',
  cheminee: 'Cheminée',
  autres: 'Autres'
};

// Convertit une valeur de champ en chaîne lisible pour l'historique
// - Pour elementsSurToiture (objet) : retourne "Velux + Cheminée" (les éléments cochés)
// - Pour les chaînes : retourne la valeur trimée
// - Pour les autres : conversion en string
const formatFieldValueForHistory = (fieldKey, value) => {
  if (fieldKey === 'elementsSurToiture') {
    if (!value || typeof value !== 'object') return '';
    const checked = Object.keys(value)
      .filter(k => value[k] === true)
      .map(k => ELEMENT_TOIT_LABELS[k] || k);
    return checked.length > 0 ? checked.join(' + ') : '';
  }
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

// Compare 2 valeurs de champ (gère les objets pour elementsSurToiture)
// Retourne true si elles sont différentes
const fieldValuesAreDifferent = (fieldKey, oldValue, newValue) => {
  if (fieldKey === 'elementsSurToiture') {
    return formatFieldValueForHistory(fieldKey, oldValue)
      !== formatFieldValueForHistory(fieldKey, newValue);
  }
  return formatFieldValueForHistory(fieldKey, oldValue)
    !== formatFieldValueForHistory(fieldKey, newValue);
};

// Fonction utilitaire : retourne le libellé du rôle d'un utilisateur
// (lit localStorage 'user' et mappe le rôle backend vers son label affiché)
const getCurrentUserInfo = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const name = (u.prenom && u.nom) ? `${u.prenom} ${u.nom}` : 'Utilisateur';
    const role = u.role ? (ROLE_LABELS[u.role] || u.role) : '';
    return { name, role };
  } catch (e) {
    return { name: 'Utilisateur', role: '' };
  }
};

// ============================================
// CSS du mockup v8 - identique
// ============================================
const FICHE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

.fc-page * { box-sizing: border-box; margin: 0; padding: 0 }
.fc-page { font-family: 'DM Sans', sans-serif; background: transparent; color: #0f172a; min-height: 100vh }
.fc-page .page { max-width: 1400px; margin: 0 auto; padding: 24px 16px 60px }

/* HEADER */
.fc-page .header { background: #fff; border: 0.5px solid #e2e8f0; border-radius: 16px; padding: 18px 24px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between }
.fc-page .prospect-info { display: flex; align-items: center; gap: 14px }
.fc-page .avatar { width: 46px; height: 46px; border-radius: 50%; background: linear-gradient(135deg,#667eea,#764ba2); display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 600; font-size: 15px; flex-shrink: 0; letter-spacing: 0.5px }
.fc-page .prospect-name { font-size: 17px; font-weight: 600; color: #0f172a }
.fc-page .prospect-meta { font-size: 12px; color: #1f2937; margin-top: 4px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-weight: 500 }
.fc-page .prospect-meta span { display: flex; align-items: center; gap: 4px }
.fc-page .prospect-meta svg { display: block }
.fc-page .meta-copy { cursor: pointer; padding: 3px 8px; border-radius: 6px; transition: all 0.15s; user-select: none }
.fc-page .meta-copy:hover { background: #f1f5f9; color: #0f172a }
.fc-page .meta-copy:active { transform: scale(0.96) }

/* Toast "Copié" */
.fc-page .copy-toast { position: fixed; top: 24px; left: 50%; transform: translateX(-50%) translateY(-100px); background: #0f172a; color: #fff; padding: 10px 20px; border-radius: 10px; font-size: 13px; font-weight: 500; box-shadow: 0 8px 24px rgba(0,0,0,0.15); z-index: 10000; opacity: 0; transition: all 0.25s; pointer-events: none; display: flex; align-items: center; gap: 8px }
.fc-page .copy-toast.show { opacity: 1; transform: translateX(-50%) translateY(0) }
.fc-page .copy-toast svg { width: 16px; height: 16px; display: block }

/* STATUS PILL */
.fc-page .status-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; padding: 5px 12px; border-radius: 20px; letter-spacing: 0.3px; cursor: pointer; border: none; font-family: inherit; transition: all 0.15s }
.fc-page .status-pill:hover { transform: scale(1.03); box-shadow: 0 2px 8px rgba(0,0,0,0.08) }
.fc-page .status-pill svg.chevron { width: 11px; height: 11px; opacity: 0.7 }
.fc-page .s-nouveau { background: #dbeafe; color: #1e40af }
.fc-page .s-nrp { background: #f3f4f6; color: #1f2937 }
.fc-page .s-rappel { background: #fef9c3; color: #854d0e }
.fc-page .s-devis-env { background: #f3e8ff; color: #6b21a8 }
.fc-page .s-devis-sig { background: #dcfce7; color: #166534 }
.fc-page .s-perdu { background: #fee2e2; color: #991b1b }

/* BTN BACK */
.fc-page .btn-back { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 500; border: 0.5px solid #e2e8f0; background: #f8fafc; color: #475569; cursor: pointer; transition: all 0.15s; font-family: inherit }
.fc-page .btn-back:hover { background: #f1f5f9; border-color: #cbd5e1 }
.fc-page .btn-back svg { width: 14px; height: 14px }

/* BARRE DE PROGRESSION */
.fc-page .progress-bar { background: #fff; border: 0.5px solid #e2e8f0; border-radius: 16px; padding: 24px 16px 16px; margin-bottom: 16px; display: flex; align-items: flex-start; overflow-x: auto }
.fc-page .step { display: flex; flex-direction: column; align-items: center; gap: 10px; flex: 1; min-width: 90px; position: relative }
.fc-page .step-circle { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; transition: all 0.2s; position: relative; z-index: 2 }
.fc-page .step-circle.done { background: #10b981; color: #fff }
.fc-page .step-circle.active { background: #3b82f6; color: #fff; box-shadow: 0 0 0 4px #dbeafe }
.fc-page .step-circle.inactive { background: #f1f5f9; color: #94a3b8 }
.fc-page .step-circle svg { width: 14px; height: 14px; display: block }
.fc-page .step-label { font-size: 11px; font-weight: 500; text-align: center; line-height: 1.3; padding: 0 4px }
.fc-page .step-label.done { color: #10b981 }
.fc-page .step-label.active { color: #3b82f6 }
.fc-page .step-label.inactive { color: #94a3b8 }
.fc-page .step-line { position: absolute; top: 16px; left: 50%; width: 100%; height: 2px; border-radius: 2px; z-index: 1 }
.fc-page .step-line.done { background: #10b981 }
.fc-page .step-line.inactive { background: #e2e8f0 }
.fc-page .step:last-child .step-line { display: none }

/* ONGLETS */
.fc-page .tabs { background: #fff; border: 0.5px solid #e2e8f0; border-radius: 16px 16px 0 0; padding: 0 12px; display: flex; gap: 2px; overflow-x: auto; border-bottom: none }
.fc-page .tab { padding: 14px 18px 11px; font-size: 13px; font-weight: 500; color: #94a3b8; cursor: pointer; transition: all 0.15s; white-space: nowrap; display: flex; align-items: center; gap: 8px; background: transparent; font-family: inherit }
.fc-page .tab:hover { color: #475569 }
.fc-page .tab svg { width: 14px; height: 14px; display: block; margin-bottom: 5px }
.fc-page .tab.active { font-weight: 700 }
.fc-page .tab .tab-label { position: relative; padding-bottom: 5px }
.fc-page .tab.active .tab-label::after { content: ''; position: absolute; bottom: -3px; left: 0; right: 0; height: 3px; border-radius: 2px 2px 0 0 }
.fc-page .tab[data-color="blue"].active { color: #1d4ed8 }
.fc-page .tab[data-color="blue"].active .tab-label::after { background: #3b82f6 }
.fc-page .tab[data-color="green"].active { color: #15803d }
.fc-page .tab[data-color="green"].active .tab-label::after { background: #16a34a }
.fc-page .tab[data-color="amber"].active { color: #ca8a04 }
.fc-page .tab[data-color="amber"].active .tab-label::after { background: #eab308 }
.fc-page .tab[data-color="orange"].active { color: #6b21a8 }
.fc-page .tab[data-color="orange"].active .tab-label::after { background: #9333ea }
.fc-page .tab[data-color="slate"].active { color: #334155 }
.fc-page .tab[data-color="slate"].active .tab-label::after { background: #475569 }
.fc-page .tab[data-color="emerald"].active { color: #db2777 }
.fc-page .tab[data-color="emerald"].active .tab-label::after { background: #ec4899 }
.fc-page .tab[data-color="cyan"].active { color: #0e7490 }
.fc-page .tab[data-color="cyan"].active .tab-label::after { background: #1FD1FD }

/* ONGLETS VERROUILLÉS (workflow Client → Logement → Technique → Finance) */
.fc-page .tab.tab-locked { color: #cbd5e1; cursor: not-allowed; opacity: 0.55 }
.fc-page .tab.tab-locked:hover { color: #cbd5e1 }
.fc-page .tab.tab-locked svg { opacity: 0.6 }

/* CONTENU ONGLETS */
.fc-page .tab-content { background: #fff; border: 0.5px solid #e2e8f0; border-top: none; border-radius: 0 0 16px 16px; padding: 24px; margin-bottom: 14px }
.fc-page .tab-pane-placeholder { text-align: center; padding: 40px 20px; color: #94a3b8; font-size: 13px }

/* PANE TITLE (carré coloré + titre) */
.fc-page .pane-title { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 0.5px solid #f1f5f9 }
.fc-page .pane-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0 }
.fc-page .pane-icon svg { width: 16px; height: 16px; display: block }
.fc-page .pane-name { font-size: 14px; font-weight: 600; letter-spacing: 0.02em }

/* GRILLES */
.fc-page .g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px }
.fc-page .g3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px }
.fc-page .g4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 14px }
.fc-page .span2 { grid-column: span 2 }
.fc-page .mb14 { margin-bottom: 14px }

/* CHAMPS */
.fc-page .f { display: flex; flex-direction: column; gap: 5px }
.fc-page .f label { font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em }
.fc-page .f select { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; cursor: pointer }
.fc-page .f input, .fc-page .f select, .fc-page .f textarea { height: 38px; border: 0.5px solid #e2e8f0; border-radius: 10px; padding: 0 12px; font-size: 13px; font-family: 'DM Sans',sans-serif; color: #0f172a; background: #f8fafc; width: 100%; outline: none; transition: all 0.15s }
.fc-page .f input:focus, .fc-page .f select:focus, .fc-page .f textarea:focus { border-color: #3b82f6; background: #fff; box-shadow: 0 0 0 3px rgba(59,130,246,0.1) }
.fc-page .f textarea.textarea-comment { height: 160px; padding: 12px; resize: vertical; line-height: 1.5 }
.fc-page .f input.locked { background: #f1f5f9; color: #475569; cursor: not-allowed; border-color: #e2e8f0; font-weight: 500 }

/* PILLS (boutons cliquables type chauffage) */
.fc-page .pill-group { padding: 0; margin-bottom: 14px }
.fc-page .pill-group:last-child { margin-bottom: 0 }
.fc-page .pill-group .label-line { font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; padding-left: 0 }
.fc-page .pills { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 5px; padding-left: 0 }
.fc-page .pill { padding: 6px 14px; border: 0.5px solid #e2e8f0; border-radius: 20px; font-size: 12px; font-weight: 500; color: #64748b; cursor: pointer; background: #f8fafc; transition: all 0.15s; user-select: none; font-family: inherit }
.fc-page .pill:hover { border-color: #cbd5e1; background: #f1f5f9 }
.fc-page .pill.on { background: #eff6ff; border-color: #93c5fd; color: #1d4ed8 }
.fc-page .pill[data-pill-color="green"].on { background: #f0fdf4; border-color: #86efac; color: #15803d }
.fc-page .pill[data-pill-color="amber"].on { background: #fef9c3; border-color: #fde047; color: #ca8a04 }
.fc-page .pill[data-pill-color="purple"].on { background: #f3e8ff; border-color: #d8b4fe; color: #6b21a8 }
.fc-page .pill[data-pill-color="slate"].on { background: #f1f5f9; border-color: #cbd5e1; color: #334155 }
.fc-page .pill[data-pill-color="pink"].on { background: #fce7f3; border-color: #f9a8d4; color: #db2777 }

/* CHAMP CUSTOM (kW) qui apparaît si Autres est sélectionné */
.fc-page .custom-kw { display: none; align-items: center; gap: 8px; margin-top: 8px }
.fc-page .custom-kw.show { display: flex }
.fc-page .custom-kw input { width: 100px; height: 34px; border: 0.5px solid #93c5fd; border-radius: 8px; padding: 0 10px; font-size: 13px; color: #0f172a; background: #eff6ff; outline: none; font-family: inherit }
.fc-page .custom-kw span { font-size: 13px; color: #475569; font-weight: 500 }

/* COMMENTAIRES */
.fc-page .btn-save-comment { display: flex; align-items: center; gap: 6px; padding: 9px 18px; background: #10b981; color: #fff; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; cursor: pointer; margin-top: 10px; font-family: 'DM Sans',sans-serif; transition: all 0.15s }
.fc-page .btn-save-comment:hover { background: #059669 }
.fc-page .btn-save-comment svg { width: 14px; height: 14px; display: block }
.fc-page .comment-history { margin-top: 24px; padding-top: 20px; border-top: 0.5px solid #f1f5f9 }
.fc-page .comment-history-title { font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 12px }
.fc-page .comment-item { padding: 14px; background: #f8fafc; border: 0.5px solid #e2e8f0; border-radius: 12px; margin-bottom: 10px }
.fc-page .comment-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #64748b; margin-bottom: 6px; flex-wrap: wrap }
.fc-page .comment-meta strong { font-weight: 600; color: #0f172a }
.fc-page .comment-meta .dot { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; display: inline-block }
.fc-page .comment-meta .comment-role { color: #475569; font-weight: 500 }
.fc-page .comment-text { font-size: 13px; color: #0f172a; line-height: 1.5 }
.fc-page .comment-empty { text-align: center; padding: 32px 20px; color: #94a3b8; font-size: 13px }

/* TIMELINE HISTORIQUE */
.fc-page .tl-item { display: flex; gap: 12px; padding: 12px 0; border-bottom: 0.5px solid #f1f5f9 }
.fc-page .tl-item:last-child { border-bottom: none }
.fc-page .tl-icon { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0 }
.fc-page .tl-icon svg { width: 14px; height: 14px; display: block }
.fc-page .tl-text { font-size: 13px; color: #0f172a; line-height: 1.4 }
.fc-page .tl-text strong { font-weight: 600 }
.fc-page .tl-meta { font-size: 11px; color: #94a3b8; margin-top: 3px; display: flex; align-items: center; gap: 6px }
.fc-page .tl-dot { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; display: inline-block }
.fc-page .tl-role { color: #db2777; font-weight: 500 }

/* PAGINATION HISTORIQUE */
.fc-page .hist-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 20px; padding-top: 16px; border-top: 0.5px solid #f1f5f9 }
.fc-page .hist-page-btn { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 500; border: 0.5px solid #e2e8f0; background: #fff; color: #475569; cursor: pointer; transition: all 0.15s; font-family: inherit }
.fc-page .hist-page-btn:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; color: #0f172a }
.fc-page .hist-page-btn:disabled { opacity: 0.4; cursor: not-allowed }
.fc-page .hist-page-btn svg { width: 12px; height: 12px }
.fc-page .hist-page-info { font-size: 12px; color: #64748b; font-weight: 500; min-width: 80px; text-align: center }

/* MODALE POP-UP */
.fc-page { position: relative }
.fc-page .modal-overlay { display: none; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999; padding: 20px; align-items: flex-start; justify-content: center; overflow-y: auto; min-height: 100vh }
.fc-page .modal-overlay.show { display: flex; animation: fadeIn 0.2s }
@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
.fc-page .modal { background: #fff; border-radius: 12px; padding: 24px; max-width: 480px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.2); margin-top: 200px; max-height: calc(100vh - 240px); overflow-y: auto }
.fc-page .modal-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px }
.fc-page .modal-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0 }
.fc-page .modal-icon svg { width: 20px; height: 20px; display: block }
.fc-page .modal-title { font-size: 16px; font-weight: 600; color: #0f172a }
.fc-page .modal-subtitle { font-size: 12px; color: #64748b; margin-top: 2px }
.fc-page .modal-body { margin-bottom: 20px }
.fc-page .modal-actions { display: flex; gap: 10px; justify-content: flex-end; padding-top: 16px; border-top: 0.5px solid #f1f5f9 }
.fc-page .btn-cancel { padding: 10px 18px; border-radius: 10px; border: 0.5px solid #e2e8f0; background: #f8fafc; color: #475569; cursor: pointer; font-size: 13px; font-weight: 500; font-family: 'DM Sans',sans-serif; transition: all 0.15s }
.fc-page .btn-cancel:hover { background: #f1f5f9 }
.fc-page .btn-confirm { padding: 10px 18px; border-radius: 10px; border: none; background: #3b82f6; color: #fff; cursor: pointer; font-size: 13px; font-weight: 500; font-family: 'DM Sans',sans-serif; transition: all 0.15s }
.fc-page .btn-confirm:hover { background: #2563eb }

/* MODALE CHANGEMENT DE STATUT - Identique au CRM original */
.fc-page .modal-status { background: #fff; border-radius: 12px; max-width: 448px; width: 100%; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04); margin-top: 80px; max-height: 600px; display: flex; flex-direction: column; padding: 24px }
.fc-page .modal-status-title { font-size: 18px; font-weight: 600; color: #111827; margin-bottom: 16px; flex-shrink: 0 }
.fc-page .modal-status-body { overflow-y: auto; flex: 1; padding-right: 8px; margin-right: -8px }
.fc-page .status-section { margin-bottom: 16px }
.fc-page .status-section:last-child { margin-bottom: 0 }
.fc-page .status-section-title { font-size: 14px; font-weight: 500; margin-bottom: 8px; display: flex; align-items: center; gap: 8px }
.fc-page .status-section-title svg { width: 16px; height: 16px; flex-shrink: 0 }
.fc-page .status-section-title.opp { color: #15803d }
.fc-page .status-section-title.suivi { color: #c2410c }
.fc-page .status-section-title.perdu { color: #b91c1c }
.fc-page .status-list { display: flex; flex-direction: column; gap: 4px }
.fc-page .status-row { width: 100%; text-align: left; padding: 8px 12px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; transition: background-color 0.15s, border-color 0.15s; display: flex; align-items: center; gap: 8px; position: relative; overflow: hidden; background: transparent; font-family: inherit }
.fc-page .status-row.opp:hover:not(.disabled) { background: #f0fdf4; border-color: #bbf7d0 }
.fc-page .status-row.suivi:hover:not(.disabled) { background: #fff7ed; border-color: #fed7aa }
.fc-page .status-row.perdu:hover:not(.disabled) { background: #fef2f2; border-color: #fecaca }
.fc-page .status-row.disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; opacity: 0.5 }
.fc-page .status-row .row-bar { position: absolute; left: 0; top: 0; bottom: 0; width: 3px }
.fc-page .status-row .row-icon { width: 16px; height: 16px; margin-left: 4px; flex-shrink: 0 }
.fc-page .status-row .row-icon svg { width: 16px; height: 16px; display: block }
.fc-page .status-row .row-label { font-size: 14px; color: #111827 }
.fc-page .status-row.disabled .row-label { color: #9ca3af }
.fc-page .status-row .row-actuel { margin-left: auto; font-size: 12px; color: #6b7280 }
.fc-page .modal-status-footer { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb; flex-shrink: 0 }
.fc-page .btn-modal-cancel { padding: 8px 16px; border: none; background: transparent; color: #4b5563; font-size: 14px; cursor: pointer; font-family: 'DM Sans',sans-serif; transition: color 0.15s }
.fc-page .btn-modal-cancel:hover { color: #1f2937 }

/* NAVIGATION ONGLETS (bouton précédent / suivant) */
.fc-page .tab-nav { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; padding-top: 20px; border-top: 0.5px solid #f1f5f9 }
.fc-page .btn-prev, .fc-page .btn-next { display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 9px; font-size: 14px; font-weight: 500; font-family: 'DM Sans',sans-serif; cursor: pointer; transition: all 0.15s; border: none; white-space: nowrap }
.fc-page .btn-prev svg, .fc-page .btn-next svg { width: 14px; height: 14px; flex-shrink: 0 }
.fc-page .btn-prev { background: #f1f5f9; color: #475569 }
.fc-page .btn-prev:hover { background: #e2e8f0 }
.fc-page .btn-prev:disabled { opacity: 0.4; cursor: not-allowed }
.fc-page .btn-next { background: #3b82f6; color: #fff; margin-left: auto }
.fc-page .btn-next:hover:not(:disabled) { background: #2563eb }
.fc-page .btn-next:disabled { background: #cbd5e1; color: #f8fafc; cursor: not-allowed; opacity: 0.7 }

/* INDICATEUR AUTO-SAVE */
.fc-page .save-indicator { position: fixed; bottom: 20px; right: 20px; padding: 8px 14px; background: #10b981; color: #fff; border-radius: 20px; font-size: 12px; font-weight: 500; box-shadow: 0 4px 12px rgba(16,185,129,0.3); opacity: 0; transition: opacity 0.3s; z-index: 50 }
.fc-page .save-indicator.show { opacity: 1 }

/* ZONE BAS FIN */
`;

// ============================================
// Configuration des 6 étapes barre de progression
// ============================================
const STEPS = [
  { label: 'Nouveau prospect', short: 'Nouveau<br/>prospect' },
  { label: 'Devis envoyé', short: 'Devis<br/>envoyé' },
  { label: 'Devis signé', short: 'Devis<br/>signé' },
  { label: 'Installation programmée', short: 'Install.<br/>programmée' },
  { label: 'Installation confirmée', short: 'Install.<br/>confirmée' },
  { label: 'Client installé', short: 'Client<br/>installé' }
];

const STEP_ICONS = [
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
];

// ============================================
// Configuration des onglets
// ============================================
const TABS = [
  { id: 'client', label: 'Client', color: 'blue', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg> },
  { id: 'logement', label: 'Logement', color: 'green', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { id: 'technique', label: 'Technique', color: 'amber', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg> },
  { id: 'finance', label: 'Finance', color: 'orange', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M19 5a8 8 0 1 0 0 14"/><line x1="3" y1="10" x2="13" y2="10"/><line x1="3" y1="14" x2="13" y2="14"/></svg> },
  { id: 'suivi', label: 'Suivi', color: 'cyan', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
  { id: 'commentaires', label: 'Commentaires', color: 'slate', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
  { id: 'chantier', label: 'Chantier', color: 'teal', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> },
  { id: 'documents', label: 'Documents', color: 'slate', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
  { id: 'historique', label: 'Historique', color: 'emerald', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> }
];

// ============================================
// LISTE DES 16 STATUTS avec couleurs et icônes du CRM
// ============================================
const ICON_USERPLUS = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>;
const ICON_CALENDAR = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const ICON_FILE = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const ICON_CHECK = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const ICON_CLOCK = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const ICON_WRENCH = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
const ICON_HOME = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const ICON_PHONE_OFF = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.42 19.42 0 01-3.33-2.67m-2.67-3.34a19.79 19.79 0 01-3.07-8.63A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91"/><line x1="23" y1="1" x2="1" y2="23"/></svg>;
const ICON_X = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
const ICON_TARGET = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
const ICON_ALERT = <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;

// Couleurs barres latérales (identiques au CRM original)
const STATUS_COLORS = {
  'Nouveau prospect': '#C5DFF4',
  'Ne répond pas': '#FFE59F',
  'Ne répond pas 2': '#FFE59F',
  'Ne répond pas 3': '#FFE59F',
  'Ne répond pas 3+': '#FFE59F',
  'À rappeler': '#FDE68A',
  'Devis envoyé': '#E9D5FF',
  'Devis signé': '#D1FAE5',
  'Installation programmée': '#A7F3D0',
  'Installation confirmée': '#6EE7B7',
  'Client installé': '#34D399',
  'Faux numéro': '#FECACA',
  'Hors cible': '#FCA5A5',
  'Pas intéressé': '#F87171',
  'Annulé': '#EF4444'
};

const STATUSES_OPPORTUNITES = [
  { label: 'Nouveau prospect', icon: ICON_USERPLUS },
  { label: 'Devis envoyé', icon: ICON_FILE },
  { label: 'Devis signé', icon: ICON_CHECK },
  { label: 'Installation programmée', icon: ICON_CLOCK },
  { label: 'Installation confirmée', icon: ICON_WRENCH },
  { label: 'Client installé', icon: ICON_HOME }
];
const STATUSES_SUIVI = [
  { label: 'À rappeler', icon: ICON_CALENDAR },
  { label: 'Ne répond pas', icon: ICON_PHONE_OFF },
  { label: 'Ne répond pas 2', icon: ICON_PHONE_OFF },
  { label: 'Ne répond pas 3', icon: ICON_PHONE_OFF },
  { label: 'Ne répond pas 3+', icon: ICON_PHONE_OFF }
];
const STATUSES_PERDUS = [
  { label: 'Faux numéro', icon: ICON_PHONE_OFF },
  { label: 'Hors cible', icon: ICON_TARGET },
  { label: 'Pas intéressé', icon: ICON_X },
  { label: 'Annulé', icon: ICON_ALERT }
];

const FicheRdvPageV2 = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const role = user?.role;
  const estAdmin = role === 'admin';
  const estTechnicien = role === 'technicien';
  const lectureSeuleChantier = !estAdmin && !estTechnicien;

  // Onglets accessibles selon le poste
  const ONGLETS_TECHNICIEN = ['client', 'logement', 'technique', 'chantier'];
  const tabsVisibles = estTechnicien
    ? TABS.filter((t) => ONGLETS_TECHNICIEN.includes(t.id))
    : TABS;
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const formInitIdRef = useRef(null); // fiche deja initialisee pour ce prospect (ne pas ecraser les saisies)
  const prevPuissTechRef = useRef(null); // derniere puissance Technique vue (pour la recopie vers Finance)
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('client');
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'
  const [commentaires, setCommentaires] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [historique, setHistorique] = useState([]);
  const [historiquePage, setHistoriquePage] = useState(1);
  const [showPopupDevisEnv, setShowPopupDevisEnv] = useState(false);
  const [showPopupDevisSig, setShowPopupDevisSig] = useState(false);
  const [showPopupInstall, setShowPopupInstall] = useState(false);
  const [showModalStatus, setShowModalStatus] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [copyToast, setCopyToast] = useState('');

  // Ref pour stocker la valeur précédente "stabilisée" de formData
  // Sert au tracking des modifications de champs (comparaison ancien vs nouveau)
  // Initialisé à null = on n'a pas encore de baseline (pas de tracking actif)
  const prevFormDataRef = useRef(null);
  // Flag : true tant qu'on n'a pas chargé la baseline depuis MongoDB
  // Tant que false, on ne logge AUCUNE modif (sinon le pré-remplissage initial créerait des entrées)
  const trackingArmedRef = useRef(false);

  // État du formulaire (Client + Logement + Technique pour cette étape)
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    heure: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    collaborateur: '',
    nom: '', prenom: '', tel: '', mail: '',
    adresse: '', codePostal: '', ville: '',
    // Logement
    tempsProprietaire: '', ageMaison: '', surfaceHabitable: '', chargesAnnuelles: '',
    modeChauffage: '',
    // Technique
    alimentationCompteur: '', orientationToit: '', inclinaisonToiture: '',
    elementsSurToiture: { velux: false, chienAssis: false, cheminee: false, autres: false },
    revetementToit: '', puissanceInstallation: '', puissanceCustomKw: '',
    // Finance
    numeroDevis: '', prixVendu: '', puissanceKwc: '', modePaiement: '', dateDevis: '', dateSignature: '', dateInstallation: ''
  });

  // Format téléphone
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/[\s.-]/g, '');
    if (cleaned.startsWith('+33')) cleaned = '0' + cleaned.substring(3);
    if (cleaned.startsWith('0033')) cleaned = '0' + cleaned.substring(4);
    cleaned = cleaned.replace(/\D/g, '');
    if (cleaned.length === 9 && !cleaned.startsWith('0')) cleaned = '0' + cleaned;
    if (cleaned.length === 10) return cleaned.match(/.{1,2}/g).join(' ');
    return phone;
  };

  const getStatusClass = (s) => {
    if (!s) return 's-nouveau';
    if (s === 'Nouveau prospect') return 's-nouveau';
    if (s.startsWith('Ne répond pas')) return 's-nrp';
    if (s === 'À rappeler') return 's-rappel';
    if (s === 'Devis envoyé') return 's-devis-env';
    if (s === 'Devis signé' || s.includes('Installation') || s === 'Client installé') return 's-devis-sig';
    return 's-perdu';
  };

  const getCurrentStepIdx = (status) => {
    const idx = STEPS.findIndex(s => s.label === status);
    return idx >= 0 ? idx : 0;
  };

  // Charger le prospect
  useEffect(() => {
    const fetchProspect = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/mes-prospects/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProspect(data);
          if (data.commentaires_list && Array.isArray(data.commentaires_list)) {
            setCommentaires(data.commentaires_list);
          }
          if (data.historique && Array.isArray(data.historique)) {
            setHistorique(data.historique);
          }
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProspect();
  }, [id]);

  // Pré-remplir avec les données du prospect
  useEffect(() => {
    if (prospect) {
      // Ne reconstruire le formulaire qu'au PREMIER chargement de cette fiche :
      // les mises a jour suivantes du prospect (changement de statut, refresh)
      // ne doivent JAMAIS ecraser ce que l'utilisateur vient de saisir
      const pid = prospect.id || id;
      if (formInitIdRef.current === pid) return;
      formInitIdRef.current = pid;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const collabName = currentUser.prenom && currentUser.nom ? `${currentUser.prenom} ${currentUser.nom}` : 'Commercial';
      const newFormData = {
        date: new Date().toISOString().split('T')[0],
        heure: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        collaborateur: collabName,
        nom: prospect.nom || '',
        prenom: prospect.prenom || '',
        tel: formatPhoneNumber(prospect.telephone || prospect.numero_telephone || ''),
        mail: prospect.email || prospect.mail || '',
        adresse: prospect.adresse || '',
        codePostal: prospect.codePostal || prospect.code_postal || '',
        ville: prospect.ville || '',
        tempsProprietaire: prospect.temps_proprietaire || '',
        ageMaison: prospect.age_maison || '',
        surfaceHabitable: prospect.surface_habitable || '',
        chargesAnnuelles: prospect.charges_annuelles || '',
        modeChauffage: prospect.mode_chauffage || '',
        alimentationCompteur: prospect.alimentation_compteur || '',
        orientationToit: prospect.orientation_toit || '',
        inclinaisonToiture: prospect.inclinaison_toiture || '',
        elementsSurToiture: prospect.elements_sur_toiture || { velux: false, chienAssis: false, cheminee: false, autres: false },
        revetementToit: prospect.revetement_toit || '',
        puissanceInstallation: prospect.puissance_installation || '',
        puissanceCustomKw: prospect.puissance_custom_kw || '',
        numeroDevis: prospect.numero_devis || '',
        prixVendu: prospect.prix_vendu || '',
        puissanceKwc: prospect.puissance_kwc || '',
        modePaiement: prospect.mode_paiement || '',
        dateDevis: prospect.date_devis || '',
        dateSignature: prospect.date_signature || '',
        dateInstallation: prospect.date_installation || ''
      };
      setFormData(prev => ({ ...prev, ...newFormData }));

      // Stocker la baseline pour le tracking des modifications
      // Et ARMER le tracking : à partir de maintenant, toute modif sera loggée
      prevFormDataRef.current = newFormData;
      trackingArmedRef.current = true;
      prevPuissTechRef.current = `${newFormData.puissanceInstallation}|${newFormData.puissanceCustomKw}`;
    }
  }, [prospect]);

  // Détection auto pop-up "Devis envoyé"
  useEffect(() => {
    if (!prospect) return;
    const statut = prospect.status || prospect.statut || '';
    if (statut === 'Devis envoyé' && !prospect.numero_devis) {
      setShowPopupDevisEnv(true);
    }
    if (statut === 'Devis signé' && !prospect.date_signature) {
      setShowPopupDevisSig(true);
    }
    if (statut === 'Client installé' && !prospect.date_installation) {
      setShowPopupInstall(true);
    }
  }, [prospect]);

  // Auto-save universel (debounce 1s)
  // Optionnel : override pour forcer un save immédiat avec des données complémentaires
  const autoSave = async (override = {}, historiqueEvent = null) => {
    try {
      setSaveStatus('saving');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const data = {
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.tel,
        email: formData.mail,
        adresse: formData.adresse,
        code_postal: formData.codePostal,
        ville: formData.ville,
        temps_proprietaire: formData.tempsProprietaire,
        age_maison: formData.ageMaison,
        surface_habitable: formData.surfaceHabitable,
        charges_annuelles: formData.chargesAnnuelles,
        mode_chauffage: formData.modeChauffage,
        alimentation_compteur: formData.alimentationCompteur,
        orientation_toit: formData.orientationToit,
        inclinaison_toiture: formData.inclinaisonToiture,
        elements_sur_toiture: formData.elementsSurToiture,
        revetement_toit: formData.revetementToit,
        puissance_installation: formData.puissanceInstallation,
        puissance_custom_kw: formData.puissanceCustomKw,
        numero_devis: formData.numeroDevis,
        prix_vendu: formData.prixVendu,
        puissance_kwc: formData.puissanceKwc,
        mode_paiement: formData.modePaiement,
        date_devis: formData.dateDevis,
        date_signature: formData.dateSignature,
        date_installation: formData.dateInstallation,
        ...override
      };
      // Ajouter à l'historique si un événement est passé
      if (historiqueEvent) {
        const userInfo = getCurrentUserInfo();
        const newEvent = {
          action: historiqueEvent,
          user: userInfo.name,
          user_role: userInfo.role,
          date: new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          timestamp: new Date().toISOString()
        };
        data.historique = [newEvent, ...(historique || [])];
        setHistorique(data.historique);
      }
      await fetch(`${BACKEND_URL}/api/prospects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 1500);
    } catch (error) {
      console.error('Auto-save:', error);
      setSaveStatus('');
    }
  };

  useEffect(() => {
    if (!prospect || loading) return;
    const t = setTimeout(() => { autoSave(); }, 1000);
    return () => clearTimeout(t);
  }, [formData]);

  // ============================================
  // TRACKING DES MODIFICATIONS DE CHAMPS (Livrable 3)
  // ============================================
  // Compare formData actuel avec la baseline (prevFormDataRef)
  // Pour chaque champ tracké modifié dont la valeur précédente n'était PAS vide :
  //   → crée un événement historique "Champ : Ancien → Nouveau"
  // Le debounce (1200ms) attend que l'utilisateur ait fini de taper avant de logger
  useEffect(() => {
    if (!prospect || loading) return;
    if (!trackingArmedRef.current) return;
    if (!prevFormDataRef.current) return;

    const t = setTimeout(async () => {
      const previous = prevFormDataRef.current;
      if (!previous) return;

      // Détecter tous les champs modifiés
      const modifsDetectees = [];
      Object.keys(FIELD_LABELS).forEach(fieldKey => {
        const oldValue = previous[fieldKey];
        const newValue = formData[fieldKey];
        if (!fieldValuesAreDifferent(fieldKey, oldValue, newValue)) return;

        const oldDisplay = formatFieldValueForHistory(fieldKey, oldValue);
        const newDisplay = formatFieldValueForHistory(fieldKey, newValue);

        // Règle métier : on ne track PAS la 1ère saisie (vide → valeur)
        // On ne track QUE les modifications (valeur → autre valeur, y compris valeur → vide)
        if (oldDisplay === '') return;

        modifsDetectees.push({
          fieldKey,
          label: FIELD_LABELS[fieldKey],
          oldValue: oldDisplay,
          newValue: newDisplay
        });
      });

      if (modifsDetectees.length === 0) return;

      // Créer un événement historique par modification
      const userInfo = getCurrentUserInfo();
      const now = new Date();
      const dateStr = now.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const timestampISO = now.toISOString();

      const newEvents = modifsDetectees.map(m => ({
        action: m.newValue
          ? `${m.label} : ${m.oldValue} → ${m.newValue}`
          : `${m.label} : ${m.oldValue} → (vide)`,
        user: userInfo.name,
        user_role: userInfo.role,
        date: dateStr,
        timestamp: timestampISO
      }));

      // Mise à jour optimiste de l'historique local + sauvegarde en backend
      const newHistorique = [...newEvents, ...(historique || [])];
      setHistorique(newHistorique);

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        await fetch(`${BACKEND_URL}/api/prospects/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ historique: newHistorique })
        });
      } catch (err) {
        console.error('Erreur sauvegarde historique modifs:', err);
      }

      // Mettre à jour la baseline avec les nouvelles valeurs
      prevFormDataRef.current = { ...formData };
    }, 1200);

    return () => clearTimeout(t);
  }, [formData, prospect, loading]);

  // Détection "Fiche client complétée"
  // Se déclenche quand Client + Logement + Technique sont remplis pour la 1ère fois
  // L'événement n'est créé qu'une seule fois (vérification dans l'historique existant)
  useEffect(() => {
    if (!prospect || loading) return;

    const tousRemplis = isTabComplete('client') && isTabComplete('logement') && isTabComplete('technique');
    if (!tousRemplis) return;

    // Vérifier qu'on n'a pas déjà cet événement dans l'historique
    const dejaPresent = (historique || []).some(e => {
      const action = e && (e.action || '') || '';
      return action.includes('Fiche client complétée');
    });
    if (dejaPresent) return;

    // Créer l'événement (avec un petit délai pour laisser l'auto-save écrire d'abord)
    const t = setTimeout(() => {
      autoSave({}, 'Fiche client complétée');
    }, 1500);
    return () => clearTimeout(t);
  }, [formData, prospect, loading, historique]);

  // Recopie automatique : la puissance saisie en Technique alimente le champ Finance (kWc)
  useEffect(() => {
    if (prevPuissTechRef.current === null) return; // fiche pas encore chargee
    const tech = `${formData.puissanceInstallation}|${formData.puissanceCustomKw}`;
    if (prevPuissTechRef.current === tech) return; // pas de changement cote Technique
    prevPuissTechRef.current = tech;
    let kw = '';
    if (formData.puissanceInstallation === 'Autres') {
      kw = (formData.puissanceCustomKw || '').toString().replace(',', '.');
    } else {
      const m = (formData.puissanceInstallation || '').match(/(\d+[.,]?\d*)/);
      if (m) kw = m[1].replace(',', '.');
    }
    if (kw && kw !== formData.puissanceKwc) {
      setFormData(prev => ({ ...prev, puissanceKwc: kw }));
    }
  }, [formData.puissanceInstallation, formData.puissanceCustomKw]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const togglePill = (field, value) => setFormData(prev => ({ ...prev, [field]: prev[field] === value ? '' : value }));
  const toggleMultiPill = (key) => setFormData(prev => ({
    ...prev,
    elementsSurToiture: { ...prev.elementsSurToiture, [key]: !prev.elementsSurToiture[key] }
  }));

  // Ajouter un nouveau commentaire (sauvegarde en backend MongoDB)
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    // Récupérer les infos de l'utilisateur connecté (nom + rôle)
    const userInfo = getCurrentUserInfo();
    const author = userInfo.name;
    const author_role = userInfo.role;
    const date = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const timestamp = new Date().toISOString();
    const text = newComment.trim();

    const newEntry = { author, author_role, date, timestamp, text };
    const newList = [newEntry, ...(commentaires || [])];

    // Mise à jour optimiste : on affiche tout de suite et on vide la zone de saisie
    setCommentaires(newList);
    setNewComment('');

    // Sauvegarde en backend
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/prospects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ commentaires_list: newList })
      });
      if (!response.ok) {
        throw new Error('Erreur backend ' + response.status);
      }
    } catch (error) {
      console.error('Erreur sauvegarde commentaire :', error);
      // Rollback : on retire le commentaire ajouté
      setCommentaires(prev => (prev || []).filter(c => c.timestamp !== timestamp));
      // On remet le texte dans la zone de saisie pour que l'utilisateur puisse réessayer
      setNewComment(text);
    }
  };

  // Copier dans le presse-papier
  const handleCopy = (text, label) => {
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopyToast(`${label} copié !`);
        setTimeout(() => setCopyToast(''), 1800);
      }).catch(() => {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(ta);
        setCopyToast(`${label} copié !`);
        setTimeout(() => setCopyToast(''), 1800);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      setCopyToast(`${label} copié !`);
      setTimeout(() => setCopyToast(''), 1800);
    }
  };

  // Vérifier si un statut "Ne répond pas" est disponible (verrouillage progressif)
  const isStatusAvailable = (label) => {
    const neRepondPasStatuts = {
      'Ne répond pas': 1,
      'Ne répond pas 2': 2,
      'Ne répond pas 3': 3,
      'Ne répond pas 3+': 4
    };
    if (label in neRepondPasStatuts) {
      const niveauDemande = neRepondPasStatuts[label];
      const niveauActuel = (prospect && prospect.niveau_ne_repond_pas !== undefined) ? prospect.niveau_ne_repond_pas : 0;
      return niveauDemande === niveauActuel + 1;
    }
    return true;
  };

  // Changer le statut du prospect (appelle l'API)
  const handleChangeStatus = async (newStatus) => {
    if (changingStatus) return;
    setChangingStatus(true);
    const oldStatus = currentStatus;
    // Mise à jour optimiste : on met à jour l'UI tout de suite
    const oldProspect = prospect;
    setProspect({ ...prospect, status: newStatus, statut: newStatus });
    setShowModalStatus(false);
    // Ajouter à l'historique local
    const userInfo = getCurrentUserInfo();
    const newEvent = {
      action: `Statut changé : ${oldStatus} → ${newStatus}`,
      user: userInfo.name,
      user_role: userInfo.role,
      date: new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date().toISOString()
    };
    const newHistorique = [newEvent, ...(historique || [])];
    setHistorique(newHistorique);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/prospects/mes-prospects/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus, commentaire: null })
      });
      if (!response.ok) throw new Error('Erreur statut');
      // Sauvegarder l'historique
      await fetch(`${BACKEND_URL}/api/prospects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ historique: newHistorique })
      });
      // Recharger le prospect pour récupérer le niveau_ne_repond_pas mis à jour
      const refreshResponse = await fetch(`${BACKEND_URL}/api/mes-prospects/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setProspect(data);
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
      // Rollback
      setProspect(oldProspect);
    } finally {
      setChangingStatus(false);
    }
  };

  // ============================================
  // VERROUILLAGE PROGRESSIF DES ONGLETS
  // L'onglet suivant n'est accessible que si l'actuel est complet
  // ============================================

  // Vérifie qu'une chaîne est non-vide (après trim)
  const isFilled = (v) => typeof v === 'string' && v.trim().length > 0;

  // Vérifie si un onglet est complet (tous ses champs obligatoires sont remplis)
  const isTabComplete = (tabId) => {
    if (tabId === 'client') {
      return isFilled(formData.prenom)
        && isFilled(formData.nom)
        && isFilled(formData.tel)
        && isFilled(formData.mail)
        && isFilled(formData.adresse)
        && isFilled(formData.codePostal)
        && isFilled(formData.ville);
    }
    if (tabId === 'logement') {
      return isFilled(formData.tempsProprietaire)
        && isFilled(formData.ageMaison)
        && isFilled(formData.surfaceHabitable)
        && isFilled(formData.chargesAnnuelles)
        && isFilled(formData.modeChauffage);
    }
    if (tabId === 'technique') {
      const elements = formData.elementsSurToiture || {};
      const auMoinsUnElement = !!(elements.velux || elements.chienAssis || elements.cheminee || elements.autres);
      return isFilled(formData.alimentationCompteur)
        && isFilled(formData.orientationToit)
        && isFilled(formData.inclinaisonToiture)
        && auMoinsUnElement
        && isFilled(formData.revetementToit)
        && isFilled(formData.puissanceInstallation);
    }
    // Finance, Commentaires, Historique : pas de verrouillage
    return true;
  };

  // Onglets soumis au verrouillage progressif (ordre obligatoire)
  // Commentaires et Historique sont TOUJOURS accessibles
  const VERROUILLES_ORDRE = ['client', 'logement', 'technique', 'finance'];

  // Vérifie si un onglet est ACCESSIBLE (cliquable)
  const isTabAccessible = (tabId) => {
    // Commentaires + Historique : toujours accessibles
    if (tabId === 'commentaires' || tabId === 'historique') return true;
    // Pour les onglets verrouillés, il faut que TOUS les onglets précédents soient complets
    const idx = VERROUILLES_ORDRE.indexOf(tabId);
    if (idx <= 0) return true; // Client est toujours accessible
    for (let i = 0; i < idx; i++) {
      if (!isTabComplete(VERROUILLES_ORDRE[i])) return false;
    }
    return true;
  };

  // Tente de cliquer sur un onglet (silencieux si bloqué)
  const handleTabClick = (tabId) => {
    if (isTabAccessible(tabId)) {
      setActiveTab(tabId);
    }
    // Sinon : silencieux, rien ne se passe
  };

  const goPrev = () => {
    const idx = TABS.findIndex(t => t.id === activeTab);
    if (idx > 0) setActiveTab(TABS[idx - 1].id);
  };
  const goNext = () => {
    const idx = TABS.findIndex(t => t.id === activeTab);
    if (idx >= TABS.length - 1) return;
    const nextId = TABS[idx + 1].id;
    // Ne fait rien si l'onglet suivant est bloqué (sécurité, en plus du bouton désactivé)
    if (!isTabAccessible(nextId)) return;
    setActiveTab(nextId);
  };

  if (loading) {
    return (
      <div className="fc-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{FICHE_CSS}</style>
        <div style={{ color: '#64748b' }}>Chargement...</div>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="fc-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{FICHE_CSS}</style>
        <div style={{ color: '#dc2626' }}>Prospect non trouvé</div>
      </div>
    );
  }

  const initials = `${(prospect.prenom || '').charAt(0)}${(prospect.nom || '').charAt(0)}`.toUpperCase();
  const tel = formatPhoneNumber(prospect.telephone || prospect.numero_telephone || '');
  const mail = prospect.email || prospect.mail || '';
  const currentStatus = prospect.status || prospect.statut || 'Nouveau prospect';
  const currentStepIdx = getCurrentStepIdx(currentStatus);
  const currentTabIdx = TABS.findIndex(t => t.id === activeTab);
  const nextTab = TABS[currentTabIdx + 1];

  return (
    <div className="fc-page">
      <style>{FICHE_CSS}</style>
      <div className="page">

        {/* HEADER */}
        <div className="header">
          <div className="prospect-info">
            <div className="avatar">{initials}</div>
            <div>
              <div className="prospect-name">{prospect.prenom} {prospect.nom}</div>
              <div className="prospect-meta">
                {tel && (
                  <span className="meta-copy" onClick={() => handleCopy(tel, 'Téléphone')} title="Cliquer pour copier">
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                    {tel}
                  </span>
                )}
                {mail && (
                  <span className="meta-copy" onClick={() => handleCopy(mail, 'Email')} title="Cliquer pour copier">
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {mail}
                  </span>
                )}
                <button className={`status-pill ${getStatusClass(currentStatus)}`} onClick={() => setShowModalStatus(true)}>
                  <span>{currentStatus}</span>
                  <svg className="chevron" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <button className="btn-back" onClick={() => navigate('/prospects-entrants')}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Retour
          </button>
        </div>

        {/* BARRE DE PROGRESSION */}
        <div className="progress-bar">
          {STEPS.map((step, idx) => {
            const cls = idx < currentStepIdx ? 'done' : idx === currentStepIdx ? 'active' : 'inactive';
            return (
              <div className="step" key={step.label}>
                <div className={`step-circle ${cls}`}>
                  {idx < currentStepIdx
                    ? <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    : STEP_ICONS[idx]
                  }
                </div>
                <span className={`step-label ${cls}`} dangerouslySetInnerHTML={{ __html: step.short }}></span>
                <div className={`step-line ${idx < currentStepIdx ? 'done' : 'inactive'}`}></div>
              </div>
            );
          })}
        </div>

        {/* ONGLETS */}
        <div className="tabs">
          {tabsVisibles.map(tab => {
            const accessible = isTabAccessible(tab.id);
            return (
              <div
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''} ${accessible ? '' : 'tab-locked'}`}
                data-color={tab.color}
                onClick={() => handleTabClick(tab.id)}
                title={accessible ? '' : 'Complétez l\'onglet précédent pour accéder à celui-ci'}
              >
                {tab.icon}
                <span className="tab-label">{tab.label}</span>
              </div>
            );
          })}
        </div>

        {/* CONTENU ONGLETS */}
        <div className="tab-content">

          {/* ============ ONGLET CLIENT ============ */}
          {activeTab === 'client' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#eff6ff' }}>
                  <svg fill="none" stroke="#3b82f6" strokeWidth="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#1d4ed8' }}>Informations client</span>
              </div>

              <div className="g4 mb14">
                <div className="f"><label>Date</label><input type="date" value={formData.date} className="locked" disabled /></div>
                <div className="f"><label>Heure</label><input type="time" value={formData.heure} className="locked" disabled /></div>
                <div className="f span2"><label>Collaborateur</label><input type="text" value={formData.collaborateur} className="locked" disabled /></div>
              </div>

              <div className="g3 mb14">
                <div className="f"><label>Prénom</label><input type="text" value={formData.prenom} onChange={(e) => handleChange('prenom', e.target.value)} /></div>
                <div className="f"><label>Nom</label><input type="text" value={formData.nom} onChange={(e) => handleChange('nom', e.target.value)} /></div>
                <div className="f"><label>Téléphone</label><input type="text" value={formData.tel} onChange={(e) => handleChange('tel', e.target.value)} /></div>
              </div>

              <div className="g3 mb14">
                <div className="f span2"><label>Email</label><input type="email" value={formData.mail} onChange={(e) => handleChange('mail', e.target.value)} /></div>
                <div className="f"><label>Code postal</label><input type="text" value={formData.codePostal} onChange={(e) => handleChange('codePostal', e.target.value)} /></div>
              </div>

              <div className="g3">
                <div className="f span2"><label>Adresse</label><input type="text" value={formData.adresse} onChange={(e) => handleChange('adresse', e.target.value)} placeholder="Adresse complète" /></div>
                <div className="f"><label>Ville</label><input type="text" value={formData.ville} onChange={(e) => handleChange('ville', e.target.value)} /></div>
              </div>

              <div className="tab-nav">
                <button className="btn-prev" disabled>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent
                </button>
                <button className="btn-next" onClick={goNext} disabled={(() => { const i = TABS.findIndex(t => t.id === activeTab); const nxt = TABS[i + 1]; return nxt ? !isTabAccessible(nxt.id) : true; })()}>
                  Suivant : Logement
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET LOGEMENT ============ */}
          {activeTab === 'logement' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#f0fdf4' }}>
                  <svg fill="none" stroke="#16a34a" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#15803d' }}>Informations du logement</span>
              </div>

              <div className="g4 mb14">
                <div className="f"><label>Propriétaire depuis</label><input type="text" value={formData.tempsProprietaire} onChange={(e) => handleChange('tempsProprietaire', e.target.value)} placeholder="ans" /></div>
                <div className="f"><label>Âge maison</label><input type="text" value={formData.ageMaison} onChange={(e) => handleChange('ageMaison', e.target.value)} placeholder="ans" /></div>
                <div className="f"><label>Surface habitable</label><input type="text" value={formData.surfaceHabitable} onChange={(e) => handleChange('surfaceHabitable', e.target.value)} placeholder="m²" /></div>
                <div className="f"><label>Charges annuelles</label><input type="text" value={formData.chargesAnnuelles} onChange={(e) => handleChange('chargesAnnuelles', e.target.value)} placeholder="€" /></div>
              </div>

              <div className="pill-group">
                <div className="label-line">Mode de chauffage</div>
                <div className="pills">
                  {['Électricité', 'Fioul', 'Gaz', 'Bois', 'Autre'].map(opt => (
                    <div key={opt} data-pill-color="green" className={`pill ${formData.modeChauffage === opt ? 'on' : ''}`} onClick={() => togglePill('modeChauffage', opt)}>{opt}</div>
                  ))}
                </div>
              </div>

              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Client
                </button>
                <button className="btn-next" onClick={goNext} disabled={(() => { const i = TABS.findIndex(t => t.id === activeTab); const nxt = TABS[i + 1]; return nxt ? !isTabAccessible(nxt.id) : true; })()}>
                  Suivant : Technique
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET TECHNIQUE ============ */}
          {activeTab === 'technique' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#fef9c3' }}>
                  <svg fill="none" stroke="#ca8a04" strokeWidth="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#ca8a04' }}>Informations techniques</span>
              </div>

              {/* Alimentation compteur */}
              <div className="pill-group">
                <div className="label-line">Alimentation compteur</div>
                <div className="pills">
                  {['Monophasé', 'Triphasé'].map(opt => (
                    <div key={opt} data-pill-color="amber" className={`pill ${formData.alimentationCompteur === opt ? 'on' : ''}`} onClick={() => togglePill('alimentationCompteur', opt)}>{opt}</div>
                  ))}
                </div>
              </div>

              {/* Orientation toit */}
              <div className="pill-group">
                <div className="label-line">Orientation du toit</div>
                <div className="pills">
                  {['Nord', 'Sud', 'Est', 'Ouest', 'Nord-Est', 'Sud-Est', 'Sud-Ouest', 'Nord-Ouest'].map(opt => (
                    <div key={opt} data-pill-color="amber" className={`pill ${formData.orientationToit === opt ? 'on' : ''}`} onClick={() => togglePill('orientationToit', opt)}>{opt}</div>
                  ))}
                </div>
              </div>

              {/* Inclinaison toiture */}
              <div className="pill-group">
                <div className="label-line">Inclinaison de la toiture</div>
                <div className="pills">
                  {['0°', '15°', '20°', '30°', '35°', '40°', '45°', 'Champ plat'].map(opt => (
                    <div key={opt} data-pill-color="amber" className={`pill ${formData.inclinaisonToiture === opt ? 'on' : ''}`} onClick={() => togglePill('inclinaisonToiture', opt)}>{opt}</div>
                  ))}
                </div>
              </div>

              {/* Éléments sur toiture (multi-select) */}
              <div className="pill-group">
                <div className="label-line">Éléments sur toiture</div>
                <div className="pills">
                  <div data-pill-color="amber" className={`pill ${formData.elementsSurToiture.velux ? 'on' : ''}`} onClick={() => toggleMultiPill('velux')}>Velux</div>
                  <div data-pill-color="amber" className={`pill ${formData.elementsSurToiture.chienAssis ? 'on' : ''}`} onClick={() => toggleMultiPill('chienAssis')}>Chien-assis</div>
                  <div data-pill-color="amber" className={`pill ${formData.elementsSurToiture.cheminee ? 'on' : ''}`} onClick={() => toggleMultiPill('cheminee')}>Cheminée</div>
                  <div data-pill-color="amber" className={`pill ${formData.elementsSurToiture.autres ? 'on' : ''}`} onClick={() => toggleMultiPill('autres')}>Autres</div>
                </div>
              </div>

              {/* Revêtement du toit */}
              <div className="pill-group">
                <div className="label-line">Revêtement du toit</div>
                <div className="pills">
                  {['Tuile standard', 'Tuile mécanique', 'Tuile ardoise', 'Autres'].map(opt => (
                    <div key={opt} data-pill-color="amber" className={`pill ${formData.revetementToit === opt ? 'on' : ''}`} onClick={() => togglePill('revetementToit', opt)}>{opt}</div>
                  ))}
                </div>
              </div>

              {/* Puissance installation (avec custom kW) */}
              <div className="pill-group">
                <div className="label-line">Puissance de l'installation</div>
                <div className="pills">
                  {['3 Kw', '6 Kw', '9 Kw', '12 Kw'].map(opt => (
                    <div key={opt} data-pill-color="amber" className={`pill ${formData.puissanceInstallation === opt ? 'on' : ''}`} onClick={() => togglePill('puissanceInstallation', opt)}>{opt}</div>
                  ))}
                  <div data-pill-color="amber" className={`pill ${formData.puissanceInstallation === 'Autres' ? 'on' : ''}`} onClick={() => togglePill('puissanceInstallation', 'Autres')}>Autres</div>
                </div>
                <div className={`custom-kw ${formData.puissanceInstallation === 'Autres' ? 'show' : ''}`}>
                  <input type="number" value={formData.puissanceCustomKw} onChange={(e) => handleChange('puissanceCustomKw', e.target.value)} placeholder="ex: 15" min="1" />
                  <span>Kw</span>
                </div>
              </div>

              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Logement
                </button>
                <button className="btn-next" onClick={goNext} disabled={(() => { const i = TABS.findIndex(t => t.id === activeTab); const nxt = TABS[i + 1]; return nxt ? !isTabAccessible(nxt.id) : true; })()}>
                  Suivant : Finance
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET FINANCE ============ */}
          {activeTab === 'finance' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#f3e8ff' }}>
                  <svg fill="none" stroke="#9333ea" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M19 5a8 8 0 1 0 0 14"/><line x1="3" y1="10" x2="13" y2="10"/><line x1="3" y1="14" x2="13" y2="14"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#6b21a8' }}>Finance</span>
              </div>

              <div className="g3 mb14">
                <div className="f"><label>Numéro de devis</label><input type="text" value={formData.numeroDevis} onChange={(e) => handleChange('numeroDevis', e.target.value)} placeholder="ex: DEV-2026-0042" /></div>
                <div className="f"><label>Prix vendu (€ TTC)</label><input type="number" value={formData.prixVendu} onChange={(e) => handleChange('prixVendu', e.target.value)} placeholder="ex: 18500" /></div>
                <div className="f"><label>Puissance installée (kWc)</label><input type="number" step="0.1" value={formData.puissanceKwc} onChange={(e) => handleChange('puissanceKwc', e.target.value)} placeholder="ex: 6.0" /></div>
                <div className="f"><label>Mode de paiement</label>
                  <select value={formData.modePaiement || ''} onChange={(e) => handleChange('modePaiement', e.target.value)}>
                    <option value="">Non renseigné</option>
                    <option value="comptant">Au comptant</option>
                    <option value="financement">Financement</option>
                  </select>
                </div>
              </div>


              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Technique
                </button>
                <button className="btn-next" onClick={() => setActiveTab('suivi')}>
                  Suivant : Suivi
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET SUIVI ============ */}
          {activeTab === 'suivi' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#cffafe' }}>
                  <svg fill="none" stroke="#0891b2" strokeWidth="1.5" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#0e7490' }}>Suivi</span>
              </div>

              <div className="g3 mb14">
                <div className="f"><label>Date du devis</label><input type="date" value={formData.dateDevis} onChange={(e) => handleChange('dateDevis', e.target.value)} /></div>
                <div className="f"><label>Date de signature</label><input type="date" value={formData.dateSignature} onChange={(e) => handleChange('dateSignature', e.target.value)} /></div>
                <div className="f"><label>Date d'installation</label><input type="date" value={formData.dateInstallation} onChange={(e) => handleChange('dateInstallation', e.target.value)} /></div>
              </div>

              <div className="tab-nav">
                <button className="btn-prev" onClick={() => setActiveTab('finance')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Finance
                </button>
                <button className="btn-next" onClick={() => setActiveTab('commentaires')}>
                  Suivant : Commentaires
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET COMMENTAIRES ============ */}
          {activeTab === 'commentaires' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#f1f5f9' }}>
                  <svg fill="none" stroke="#475569" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#334155' }}>Commentaires</span>
              </div>

              {/* Nouveau commentaire */}
              <div className="f">
                <label>Nouveau commentaire</label>
                <textarea
                  className="textarea-comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Tapez votre commentaire ici..."
                />
              </div>
              <button className="btn-save-comment" onClick={handleAddComment}>
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                Enregistrer le commentaire
              </button>

              {/* Historique des commentaires */}
              <div className="comment-history">
                <div className="comment-history-title">
                  Commentaires précédents ({commentaires.length})
                </div>
                {commentaires.length === 0 ? (
                  <div className="comment-empty">Aucun commentaire pour l'instant</div>
                ) : (
                  commentaires.map((c, idx) => (
                    <div className="comment-item" key={idx}>
                      <div className="comment-meta">
                        <strong>{c.author || 'Utilisateur'}</strong>
                        {c.author_role && <span className="comment-role">({c.author_role})</span>}
                        <span className="dot"></span>
                        <span>{c.date}</span>
                      </div>
                      <div className="comment-text">{c.text}</div>
                    </div>
                  ))
                )}
              </div>

              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Finance
                </button>
                <button className="btn-next" onClick={goNext} disabled={(() => { const i = TABS.findIndex(t => t.id === activeTab); const nxt = TABS[i + 1]; return nxt ? !isTabAccessible(nxt.id) : true; })()}>
                  Suivant : Historique
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </>
          )}

          {/* ============ ONGLET HISTORIQUE ============ */}
          {activeTab === 'historique' && (
            <>
              <div className="pane-title">
                <div className="pane-icon" style={{ background: '#fce7f3' }}>
                  <svg fill="none" stroke="#ec4899" strokeWidth="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <span className="pane-name" style={{ color: '#db2777' }}>Historique & suivi</span>
              </div>

              {(() => {
                // Filtrer les événements internes (non destinés au commercial)
                // - "Lead reçu depuis Google Ads" est un événement admin, on ne le montre pas au commercial
                const HIDDEN_ACTIONS = ['Lead reçu depuis Google Ads'];
                const visibleHistorique = (historique || []).filter(e => {
                  const action = e && (e.action || e.message || e.event) || '';
                  return !HIDDEN_ACTIONS.some(hidden => action.includes(hidden));
                });
                // Convertit une date (format ISO "2026-06-10T..." OU français "10/06/2026 10:01")
                // en nombre comparable, pour trier de façon fiable malgré les deux formats.
                const toMillis = (e) => {
                  const raw = (e && (e.timestamp || e.date || e.created_at || '')).toString().trim();
                  if (!raw) return 0;
                  // Format français JJ/MM/AAAA [HH:MM]
                  const mFr = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}))?/);
                  if (mFr) {
                    const [, jj, mm, aaaa, hh = '00', min = '00'] = mFr;
                    return new Date(`${aaaa}-${mm}-${jj}T${hh}:${min}:00`).getTime();
                  }
                  // Sinon on tente le parsing standard (ISO, etc.)
                  const t = new Date(raw).getTime();
                  return isNaN(t) ? 0 : t;
                };
                // Tri : du plus récent au plus ancien
                visibleHistorique.sort((a, b) => toMillis(b) - toMillis(a));
                // Reformate une date (ISO ou déjà française) en "JJ/MM/AAAA HH:MM"
                const formatDate = (raw) => {
                  if (!raw) return '';
                  const s = raw.toString().trim();
                  // Déjà au format français JJ/MM/AAAA (avec ou sans heure) -> on garde
                  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) return s;
                  const d = new Date(s);
                  if (isNaN(d.getTime())) return s; // format inconnu -> on n'y touche pas
                  const p = (n) => String(n).padStart(2, '0');
                  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
                };

                if (visibleHistorique.length === 0) {
                  return <div className="comment-empty">Aucun événement dans l'historique</div>;
                }

                const totalPages = Math.max(1, Math.ceil(visibleHistorique.length / HISTORY_PAGE_SIZE));
                // Borne la page actuelle pour éviter de sortir du tableau
                const safePage = Math.min(Math.max(1, historiquePage), totalPages);
                const startIdx = (safePage - 1) * HISTORY_PAGE_SIZE;
                const pageItems = visibleHistorique.slice(startIdx, startIdx + HISTORY_PAGE_SIZE);

                // Helper pour décider quoi afficher comme auteur
                // Si l'utilisateur est "Système" → "Automatique" (sans rôle entre parenthèses)
                // Sinon → "Prénom Nom (Rôle)" comme avant
                const renderAuthor = (event) => {
                  const isSysteme = event.user === 'Système';
                  if (isSysteme) {
                    return <strong>Automatique</strong>;
                  }
                  return (
                    <>
                      {event.user && <strong>{event.user}</strong>}
                      {event.user && event.user_role && <span className="tl-role">({event.user_role})</span>}
                    </>
                  );
                };

                return (
                  <div>
                    {pageItems.map((event, idx) => (
                      <div className="tl-item" key={startIdx + idx}>
                        <div className="tl-icon" style={{ background: '#dcfce7' }}>
                          <svg fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <div>
                          <div className="tl-text">{event.action || event.message || event.event || 'Événement'}</div>
                          <div className="tl-meta">
                            {renderAuthor(event)}
                            {event.user && event.date && <span className="tl-dot"></span>}
                            {event.date && <span>{formatDate(event.date)}</span>}
                            {!event.user && !event.date && event.created_at && <span>{formatDate(event.created_at)}</span>}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Pagination : visible seulement s'il y a plus d'1 page */}
                    {totalPages > 1 && (
                      <div className="hist-pagination">
                        <button
                          className="hist-page-btn"
                          onClick={() => setHistoriquePage(p => Math.max(1, p - 1))}
                          disabled={safePage === 1}
                        >
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                          Précédent
                        </button>
                        <div className="hist-page-info">Page {safePage} / {totalPages}</div>
                        <button
                          className="hist-page-btn"
                          onClick={() => setHistoriquePage(p => Math.min(totalPages, p + 1))}
                          disabled={safePage === totalPages}
                        >
                          Suivant
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : Commentaires
                </button>
              </div>
            </>
          )}

          {activeTab === 'documents' && (
            <div style={{ padding: '4px 0' }}>
              <FicheDocuments prospectId={id} role={role} />
            </div>
          )}

          {activeTab === 'chantier' && (
            <div style={{ padding: '4px 0' }}>
              <FicheChantier prospectId={id} role={role} />
            </div>
          )}

          {/* AUTRES ONGLETS - placeholders (ne devrait plus jamais s'afficher) */}
          {activeTab !== 'client' && activeTab !== 'logement' && activeTab !== 'technique' && activeTab !== 'finance' && activeTab !== 'suivi' && activeTab !== 'commentaires' && activeTab !== 'historique' && activeTab !== 'chantier' && activeTab !== 'documents' && (
            <div className="tab-pane-placeholder">
              🚧 Onglet "<strong>{TABS.find(t => t.id === activeTab).label}</strong>" — contenu à venir à l'étape {currentTabIdx + 5}
              <div className="tab-nav">
                <button className="btn-prev" onClick={goPrev}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Précédent : {TABS[currentTabIdx - 1] ? TABS[currentTabIdx - 1].label : ''}
                </button>
                {nextTab && (
                  <button className="btn-next" onClick={goNext} disabled={(() => { const i = TABS.findIndex(t => t.id === activeTab); const nxt = TABS[i + 1]; return nxt ? !isTabAccessible(nxt.id) : true; })()}>
                    Suivant : {nextTab.label}
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                )}
              </div>
            </div>
          )}

        </div>

      </div>

      {/* INDICATEUR AUTO-SAVE */}
      <div className={`save-indicator ${saveStatus === 'saved' ? 'show' : ''}`}>
        ✓ Enregistré
      </div>

      {/* TOAST COPIÉ */}
      <div className={`copy-toast ${copyToast ? 'show' : ''}`}>
        <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {copyToast}
      </div>

      {/* POP-UP DEVIS ENVOYÉ */}
      <div className={`modal-overlay ${showPopupDevisEnv ? 'show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setShowPopupDevisEnv(false); }}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon" style={{ background: '#dbeafe' }}>
              <svg fill="none" stroke="#1d4ed8" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div className="modal-title">Saisir les infos du devis</div>
              <div className="modal-subtitle">Le statut passe à "Devis envoyé"</div>
            </div>
          </div>
          <div className="modal-body">
            <div className="g2 mb14">
              <div className="f">
                <label>Numéro de devis</label>
                <input type="text" value={formData.numeroDevis} onChange={(e) => handleChange('numeroDevis', e.target.value)} placeholder="ex: DEV-2026-0042" autoFocus />
              </div>
              <div className="f">
                <label>Prix vendu (€ TTC)</label>
                <input type="number" value={formData.prixVendu} onChange={(e) => handleChange('prixVendu', e.target.value)} placeholder="ex: 18500" />
              </div>
            </div>
            <div className="g2">
              <div className="f">
                <label>Puissance kWc</label>
                <input type="number" step="0.1" value={formData.puissanceKwc} onChange={(e) => handleChange('puissanceKwc', e.target.value)} placeholder="ex: 6.0" />
              </div>
              <div className="f">
                <label>Date du devis</label>
                <input type="date" value={formData.dateDevis} onChange={(e) => handleChange('dateDevis', e.target.value)} />
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-cancel" onClick={() => setShowPopupDevisEnv(false)}>Plus tard</button>
            <button className="btn-confirm" onClick={() => {
              autoSave({}, 'Informations du devis renseignées');
              setShowPopupDevisEnv(false);
            }}>Enregistrer</button>
          </div>
        </div>
      </div>

      {/* POP-UP DEVIS SIGNÉ */}
      <div className={`modal-overlay ${showPopupDevisSig ? 'show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setShowPopupDevisSig(false); }}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon" style={{ background: '#dcfce7' }}>
              <svg fill="none" stroke="#15803d" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
            </div>
            <div>
              <div className="modal-title">Devis signé !</div>
              <div className="modal-subtitle">Saisis la date de signature</div>
            </div>
          </div>
          <div className="modal-body">
            <div className="f">
              <label>Date de signature</label>
              <input type="date" value={formData.dateSignature} onChange={(e) => handleChange('dateSignature', e.target.value)} autoFocus />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-cancel" onClick={() => setShowPopupDevisSig(false)}>Plus tard</button>
            <button className="btn-confirm" onClick={() => {
              autoSave({}, 'Date de signature renseignée');
              setShowPopupDevisSig(false);
            }} style={{ background: '#15803d' }}>Enregistrer</button>
          </div>
        </div>
      </div>

      {/* POP-UP DATE D'INSTALLATION (statut Client installé) */}
      <div className={`modal-overlay ${showPopupInstall ? 'show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setShowPopupInstall(false); }}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon" style={{ background: '#cffafe' }}>
              <svg fill="none" stroke="#0891b2" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <div>
              <div className="modal-title">Client installé !</div>
              <div className="modal-subtitle">Saisis la date d'installation</div>
            </div>
          </div>
          <div className="modal-body">
            <div className="f">
              <label>Date d'installation</label>
              <input type="date" value={formData.dateInstallation} onChange={(e) => handleChange('dateInstallation', e.target.value)} autoFocus />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-cancel" onClick={() => setShowPopupInstall(false)}>Plus tard</button>
            <button className="btn-confirm" onClick={() => {
              autoSave({}, 'Date d\'installation renseignée');
              setShowPopupInstall(false);
            }} style={{ background: '#0891b2' }}>Enregistrer</button>
          </div>
        </div>
      </div>

      {/* MODALE CHANGEMENT DE STATUT - identique au CRM original */}
      <div className={`modal-overlay ${showModalStatus ? 'show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setShowModalStatus(false); }}>
        <div className="modal-status" onClick={(e) => e.stopPropagation()}>
          <div className="modal-status-title">
            Changer le statut de {prospect.prenom} {prospect.nom}
          </div>
          <div className="modal-status-body">

            <div className="status-section">
              <div className="status-section-title opp">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                Opportunités
              </div>
              <div className="status-list">
                {STATUSES_OPPORTUNITES.map(s => {
                  const isCurrent = currentStatus === s.label;
                  const isAvailable = isStatusAvailable(s.label);
                  const isDisabled = isCurrent || !isAvailable;
                  return (
                    <button
                      key={s.label}
                      className={`status-row opp ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && handleChangeStatus(s.label)}
                      disabled={isDisabled || changingStatus}
                    >
                      <div className="row-bar" style={{ background: STATUS_COLORS[s.label] }}></div>
                      <div className="row-icon">{s.icon}</div>
                      <span className="row-label">{s.label}</span>
                      {isCurrent && <span className="row-actuel">(Actuel)</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="status-section">
              <div className="status-section-title suivi">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Suivi à relancer
              </div>
              <div className="status-list">
                {STATUSES_SUIVI.map(s => {
                  const isCurrent = currentStatus === s.label;
                  const isAvailable = isStatusAvailable(s.label);
                  const isDisabled = isCurrent || !isAvailable;
                  return (
                    <button
                      key={s.label}
                      className={`status-row suivi ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && handleChangeStatus(s.label)}
                      disabled={isDisabled || changingStatus}
                    >
                      <div className="row-bar" style={{ background: STATUS_COLORS[s.label] }}></div>
                      <div className="row-icon">{s.icon}</div>
                      <span className="row-label">{s.label}</span>
                      {isCurrent && <span className="row-actuel">(Actuel)</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="status-section">
              <div className="status-section-title perdu">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Prospects perdus
              </div>
              <div className="status-list">
                {STATUSES_PERDUS.map(s => {
                  const isCurrent = currentStatus === s.label;
                  const isDisabled = isCurrent;
                  return (
                    <button
                      key={s.label}
                      className={`status-row perdu ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && handleChangeStatus(s.label)}
                      disabled={isDisabled || changingStatus}
                    >
                      <div className="row-bar" style={{ background: STATUS_COLORS[s.label] }}></div>
                      <div className="row-icon">{s.icon}</div>
                      <span className="row-label">{s.label}</span>
                      {isCurrent && <span className="row-actuel">(Actuel)</span>}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
          <div className="modal-status-footer">
            <button className="btn-modal-cancel" onClick={() => setShowModalStatus(false)}>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FicheRdvPageV2;
